import React, { ReactElement, useContext } from 'react';
import { Link } from 'src/components/Link';
import { SageMakerLink } from 'src/components/SageMakerLink';
import { ARN, build } from '@aws-sdk/util-arn-parser';
import { OperatorInfo } from 'src/components/OperatorInfo';
import { ConsumedModelLink } from 'src/components/ConsumedModelLink';
import { SharedModelLink } from 'src/components/SharedModelLink';
import { StatusLabel } from 'src/components/StatusLabel';
import { BedrockLink } from 'src/components/BedrockLink';
import { OperatorModalContext } from 'src/context/operatorModalContext';
import {
  OperatorAction,
  OperatorActionMenu,
} from 'src/components/OperatorActionsMenu';

export interface JobVisualizationNodeProps {
  header: string;
  info: {
    id: string;
    operatorName: string;
    operatorFlowId: string;
    modelName?: string;
    modelArn?: ARN;
    region?: string;
  };
  jobType: OperatorJobType;
  status: OperatorJobStatus;
  arnList?: ARN[];
  viewSageMakerGraphHandler?: () => void;
}

export const JobVisualizationNode = ({
  header,
  info,
  status,
  jobType,
  arnList,
  viewSageMakerGraphHandler,
}: JobVisualizationNodeProps): ReactElement => {
  const { setRetryConfig, setOverrideConfig } =
    useContext(OperatorModalContext);

  const operatorActions: OperatorAction[] = [];

  if (status === 'FAILED') {
    operatorActions.push({
      name: 'Retry',
      description: 'retry operators',
      requiredPermission: 'READ_AND_LIMITED_ACTION',
      actionFn: () => {
        setRetryConfig({
          action: 'retry',
          id: info.operatorFlowId,
          operatorName: info.operatorName,
        });
      },
    });
  }

  if (status === 'FAILED' || status === 'PENDING') {
    operatorActions.push({
      name: 'Override',
      description: 'override operators',
      requiredPermission: 'ADMINISTER',
      actionFn: () => {
        setOverrideConfig({
          id: info.operatorFlowId,
          action: 'SKIPPEDOVERRIDE',
          operatorName: info.operatorName,
          operatorStatus: status,
        });
      },
    });
  }

  return (
    <div className="job-visualization-node pointer-events-auto">
      <div className="job-visualization-node__header">
        <h3>{header}</h3>
        <div className="job-visualization-node__status-container">
          <StatusLabel loading={false} label={status} />
        </div>
      </div>
      <OperatorInfo
        operatorName={info.operatorName}
        operatorId={info.id}
        region={
          info.region && info.region !== 'REGIONLESS' ? info.region : undefined
        }
      />
      <div className="job-visualization-node__links">
        {arnList?.map((arn) => {
          const [resourceType] = arn.resource.split('/');

          // SageMaker has no UI for monitoring schedules, so we don't make a link
          if (
            resourceType !== 'monitoring-schedule' &&
            resourceType !== 'pipeline'
          )
            return jobType === 'MODELCUSTOMIZATIONJOB' ? (
              <BedrockLink key={build(arn)} arn={arn} />
            ) : (
              <SageMakerLink key={build(arn)} arn={arn} />
            );
        })}
        <Link to={`operatorHistory?operatorName=${info.operatorName}`}>
          View History
        </Link>
        {jobType === 'SHAREMODELJOB' && (
          <SharedModelLink
            shareJobId={info.id}
            succeeded={status === 'SUCCEEDED'}
          />
        )}
        {jobType === 'SHAREDMODELCONSUMEJOB' && (
          <ConsumedModelLink consumeJobId={info.id} />
        )}
        {jobType === 'SAGEMAKERPIPELINEEXECUTION' && (
          <Link
            to="#nogo"
            onClick={() => {
              viewSageMakerGraphHandler?.();
            }}
          >
            View SageMaker Pipeline Graph
          </Link>
        )}
      </div>
      <div className="job-visualization-node__buttons">
        {jobType !== 'FEEDBACKLOOPJOB' && (
          <>
            {status !== 'NOTSTARTED' && (
              <Link to={`../logs?operatorName=${info.operatorName}`} newTab>
                View Logs
              </Link>
            )}

            <OperatorActionMenu actions={operatorActions} />
          </>
        )}
      </div>
    </div>
  );
};
